import { api } from './api';
import { closeDialog, closeNotifications, closePalette } from './overlays';
import { queryClient } from './queryClient';
import { clearRecent } from './recent';
import { resetRoute } from './router';
import { createStore } from './store';

// Signing out, and the message the sign-in screen shows afterwards (a Google sign-in problem, an ended session).

/** Reads and removes a sign-in problem the server put in the address (?auth_error=...), keeping everything else. */
function takeAuthError(): string | null {
  const url = new URL(window.location.href);
  const message = url.searchParams.get('auth_error');
  if (message === null) return null;
  url.searchParams.delete('auth_error');
  window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  return message || null;
}

export const sessionStore = createStore<{ notice: string | null }>({ notice: takeAuthError() });

/** Hands over the waiting notice once, so it shows a single time. */
export function takeNotice(): string | null {
  const { notice } = sessionStore.get();
  if (notice) sessionStore.set({ notice: null });
  return notice;
}

/** Clears everything this tab knew about the signed-in person and returns to the sign-in screen. */
export function endSessionLocally(notice: string | null): void {
  closeDialog();
  closePalette();
  closeNotifications();
  clearRecent();
  sessionStore.set({ notice });
  resetRoute();
  // Resetting "me" first makes the app show sign-in before anything else refetches with no session.
  void queryClient.resetQueries({ queryKey: ['me'] });
  queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
}

export async function signOut(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } catch {
    // The session ends on this device either way; the server forgets it when it expires.
  }
  endSessionLocally(null);
}

/** What the sign-in screen says when the server ends a session that was open in this tab. */
export const SESSION_ENDED = 'Your session has ended. Sign in again to carry on.';
